import React, { Component } from "react";
import { Play, Pause, Square, Check, ChevronDown, Headphones, X } from "lucide-react";

/**
 * Lecteur "Écouter l'article" — synthèse vocale via la Web Speech API
 * du navigateur (aucun backend, aucune dépendance, aucun coût).
 *
 * - Lit le texte de #article-content (le contenu déjà rendu) + le titre.
 * - Découpe en phrases et enchaîne les utterances (articles longs OK,
 *   contourne la limite de durée de speechSynthesis sur certains navigateurs).
 * - Sélecteur de voix (FR en priorité), choix mémorisé en localStorage.
 *
 * Props :
 *  - title           : titre lu en intro
 *  - contentSelector : sélecteur du conteneur texte (def. #article-content)
 */
const VOICE_STORAGE_KEY = "article-tts-voice";
const OPEN_STORAGE_KEY = "article-tts-open";

// Débit moyen d'une voix de synthèse (rate = 1) ≈ 15 caractères/seconde.
// Sert à ESTIMER une durée (la Web Speech API n'en fournit aucune).
const CHARS_PER_SEC = 15;

const formatTime = (sec) => {
    if (!Number.isFinite(sec) || sec < 0) {
        return "0:00";
    }
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
};

class ArticleAudioPlayer extends Component {
    constructor(props) {
        super(props);
        this.synth = typeof window !== "undefined" ? window.speechSynthesis : null;
        this.chunks = [];
        this.chunkOffsets = [];
        this.totalChars = 0;
        this.chunkIndex = 0;
        this.state = {
            supported: !!this.synth,
            voices: [],
            voiceURI: null,
            status: "idle", // idle | playing | paused
            voiceMenuOpen: false,
            progress: 0, // 0 → 1
            durationSec: 0, // durée estimée (s)
            // Replié par défaut ; l'utilisateur peut l'ouvrir, choix persistant.
            open: typeof window !== "undefined"
                && window.localStorage.getItem(OPEN_STORAGE_KEY) === "1"
        };
    }

    componentDidMount() {
        if (!this.synth) {
            return;
        }
        this.loadVoices();
        // Les voix arrivent parfois de façon asynchrone.
        this.synth.addEventListener?.("voiceschanged", this.loadVoices);
        document.addEventListener("mousedown", this.handleOutside);
        // Déjà ouvert au chargement : estimer la durée une fois le contenu rendu.
        if (this.state.open) {
            window.setTimeout(this.prepare, 400);
        }
    }

    componentWillUnmount() {
        if (this.synth) {
            this.synth.cancel();
            this.synth.removeEventListener?.("voiceschanged", this.loadVoices);
        }
        document.removeEventListener("mousedown", this.handleOutside);
    }

    handleOutside = (e) => {
        if (this.state.voiceMenuOpen && this.menuRef && !this.menuRef.contains(e.target)) {
            this.setState({ voiceMenuOpen: false });
        }
    };

    loadVoices = () => {
        const all = this.synth.getVoices() || [];
        if (all.length === 0) {
            return;
        }
        // FR d'abord, puis le reste.
        const fr = all.filter((v) => v.lang && v.lang.toLowerCase().startsWith("fr"));
        const others = all.filter((v) => !(v.lang && v.lang.toLowerCase().startsWith("fr")));
        const voices = [...fr, ...others];

        const saved = window.localStorage.getItem(VOICE_STORAGE_KEY);
        const current = this.state.voiceURI;
        const pick =
            (saved && voices.find((v) => v.voiceURI === saved)) ||
            (current && voices.find((v) => v.voiceURI === current)) ||
            voices[0];

        this.setState({ voices, voiceURI: pick ? pick.voiceURI : null });
    };

    getSelectedVoice = () =>
        this.state.voices.find((v) => v.voiceURI === this.state.voiceURI) || null;

    // "Microsoft Hortense - French (France)" → "Hortense"
    shortVoiceName = (voice) => {
        if (!voice) {
            return "Voix";
        }
        const cleaned = voice.name
            .replace(/^(Microsoft|Google|Apple|Amazon|Chrome OS)\s+/i, "")
            .split(/\s*[-(]/)[0]
            .trim();
        return cleaned || voice.name;
    };

    buildChunks = () => {
        const selector = this.props.contentSelector || "#article-content";
        const el = document.querySelector(selector);
        const body = el ? el.innerText : "";
        const intro = this.props.title ? `${this.props.title}. ` : "";
        const raw = `${intro}${body}`.replace(/\s+/g, " ").trim();

        // Découpe en segments lisibles (phrases / ~220 caractères).
        const sentences = raw.match(/[^.!?…]+[.!?…]*\s*/g) || [raw];
        const chunks = [];
        let buf = "";
        for (const s of sentences) {
            if ((buf + s).length > 220 && buf) {
                chunks.push(buf.trim());
                buf = s;
            } else {
                buf += s;
            }
        }
        if (buf.trim()) {
            chunks.push(buf.trim());
        }
        return chunks.filter(Boolean);
    };

    // Offsets cumulés de caractères pour calculer la progression globale.
    computeOffsets = () => {
        let acc = 0;
        this.chunkOffsets = this.chunks.map((c) => {
            const start = acc;
            acc += c.length + 1; // +1 ≈ séparateur entre segments
            return start;
        });
        this.totalChars = Math.max(1, acc);
        this.setState({ durationSec: this.totalChars / CHARS_PER_SEC });
    };

    // Prépare le texte sans lancer la lecture (pour afficher la durée estimée
    // dès l'ouverture du lecteur).
    prepare = () => {
        if (this.chunks.length === 0) {
            this.chunks = this.buildChunks();
            if (this.chunks.length > 0) {
                this.computeOffsets();
            }
        }
    };

    setProgress = (ratio) => {
        const clamped = Math.max(0, Math.min(1, ratio));
        // Évite les re-rendus inutiles (arrondi à ~0.5%).
        if (Math.abs(clamped - this.state.progress) >= 0.004 || clamped === 0 || clamped === 1) {
            this.setState({ progress: clamped });
        }
    };

    speakFrom = (index) => {
        if (!this.synth || index >= this.chunks.length) {
            this.setProgress(1);
            this.stop();
            return;
        }
        this.chunkIndex = index;
        const utter = new SpeechSynthesisUtterance(this.chunks[index]);
        const voice = this.getSelectedVoice();
        if (voice) {
            utter.voice = voice;
            utter.lang = voice.lang;
        }
        utter.rate = 1;
        utter.pitch = 1;
        utter.onboundary = (e) => {
            if (typeof e.charIndex === "number") {
                this.setProgress((this.chunkOffsets[index] + e.charIndex) / this.totalChars);
            }
        };
        utter.onend = () => {
            if (this.state.status === "playing") {
                const next = index + 1;
                this.setProgress((this.chunkOffsets[next] ?? this.totalChars) / this.totalChars);
                this.speakFrom(next);
            }
        };
        utter.onstart = () => {
            // Premier son émis : on quitte l'état "chargement".
            if (this.state.status === "loading") {
                this.setState({ status: "playing" });
            }
        };
        utter.onerror = () => this.stop();
        this.synth.speak(utter);
    };

    // Attend que le contenu de l'article soit rendu (TipTap) avant de lancer.
    startWhenReady = (attempt = 0) => {
        if (this.state.status !== "loading") {
            return; // annulé entre-temps (stop)
        }
        this.chunks = this.buildChunks();
        if (this.chunks.length === 0) {
            if (attempt < 25) {
                window.setTimeout(() => this.startWhenReady(attempt + 1), 150);
            } else {
                this.setState({ status: "idle" }); // contenu indisponible
            }
            return;
        }
        this.computeOffsets();
        this.speakFrom(0);
    };

    play = () => {
        if (!this.synth) {
            return;
        }
        if (this.state.status === "paused") {
            this.synth.resume();
            this.setState({ status: "playing" });
            return;
        }
        this.synth.cancel();
        // État "chargement" tant que le 1er son n'est pas émis.
        this.setState({ status: "loading", progress: 0 }, () => this.startWhenReady(0));
    };

    seek = (e) => {
        if (!this.synth) {
            return;
        }
        // Lecture pas encore démarrée : on prépare le texte à la volée.
        if (this.chunks.length === 0) {
            this.chunks = this.buildChunks();
            if (this.chunks.length === 0) {
                return;
            }
            this.computeOffsets();
        }
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
        const target = ratio * this.totalChars;
        // On retombe sur le début du segment correspondant.
        let idx = 0;
        for (let i = 0; i < this.chunkOffsets.length; i += 1) {
            if (this.chunkOffsets[i] <= target) {
                idx = i;
            }
        }
        this.synth.cancel();
        this.setState(
            { status: "loading", progress: this.chunkOffsets[idx] / this.totalChars },
            () => this.speakFrom(idx)
        );
    };

    pause = () => {
        if (this.synth && this.state.status === "playing") {
            this.synth.pause();
            this.setState({ status: "paused" });
        }
    };

    stop = () => {
        if (this.synth) {
            this.synth.cancel();
        }
        this.chunkIndex = 0;
        this.setState({ status: "idle", progress: 0 });
    };

    toggleOpen = () => {
        const open = !this.state.open;
        window.localStorage.setItem(OPEN_STORAGE_KEY, open ? "1" : "0");
        this.setState({ open }, () => {
            if (open) {
                // Contenu rendu : on estime la durée pour l'afficher de suite.
                window.requestAnimationFrame(this.prepare);
            }
        });
    };

    selectVoice = (voiceURI) => {
        window.localStorage.setItem(VOICE_STORAGE_KEY, voiceURI);
        const wasPlaying = this.state.status !== "idle";
        this.setState({ voiceURI, voiceMenuOpen: false }, () => {
            if (wasPlaying) {
                // Relit le segment courant avec la nouvelle voix.
                this.synth.cancel();
                this.setState({ status: "playing" }, () => this.speakFrom(this.chunkIndex));
            }
        });
    };

    render() {
        const { supported, status, voices, voiceURI, voiceMenuOpen, open } = this.state;
        if (!supported) {
            return null;
        }

        const selected = this.getSelectedVoice();
        const playing = status === "playing";
        const loading = status === "loading";
        const active = status !== "idle";
        const pct = Math.round(this.state.progress * 100);
        const { durationSec, progress } = this.state;
        const elapsedSec = progress * durationSec;

        return (
            <div className={`article-audio ${playing ? "is-playing" : ""} ${active ? "is-active" : ""} ${open ? "is-open" : "is-collapsed"}`}>
              {!open && (
              <button
                  type="button"
                  className="article-audio-head"
                  onClick={this.toggleOpen}
                  aria-expanded={open}
                  aria-label="Écouter l'article"
              >
                  <span className="article-audio-head-icon">
                      {loading ? (
                          <span className="article-audio-spinner" aria-hidden="true" />
                      ) : playing ? (
                          <span className="article-audio-eq" aria-hidden="true"><i /><i /><i /><i /></span>
                      ) : (
                          <Headphones size={18} />
                      )}
                  </span>
                  <span className="article-audio-head-label">
                      {loading ? "Préparation…" : playing ? "Lecture en cours…" : status === "paused" ? "Lecture en pause" : "Écouter l'article"}
                  </span>
                  <ChevronDown size={18} className="article-audio-chevron" />
              </button>
              )}

              {open && (
              <div className="article-audio-body">
              <div className="article-audio-bar">
                <button
                    type="button"
                    className="article-audio-play"
                    onClick={loading ? this.stop : playing ? this.pause : this.play}
                    aria-label={loading ? "Chargement (cliquer pour annuler)" : playing ? "Mettre en pause" : status === "paused" ? "Reprendre" : "Lire l'article"}
                >
                    {loading
                        ? <span className="article-audio-spinner article-audio-spinner--light" aria-hidden="true" />
                        : playing
                            ? <Pause size={20} fill="currentColor" />
                            : <Play size={20} fill="currentColor" style={{ marginLeft: 2 }} />}
                </button>

                <button
                    type="button"
                    className="article-audio-ctrl article-audio-stop"
                    onClick={this.stop}
                    disabled={!active}
                    aria-label="Arrêter"
                >
                    <Square size={15} fill="currentColor" />
                </button>

                <div
                    className="article-audio-progress"
                    onClick={this.seek}
                    role="slider"
                    aria-label="Progression de la lecture"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={pct}
                    title={`${pct}%`}
                >
                    <div className="article-audio-progress-track">
                        <div className="article-audio-progress-fill" style={{ width: `${pct}%` }}>
                            <span className="article-audio-progress-knob" />
                        </div>
                    </div>
                    <span className="article-audio-progress-time">
                        {formatTime(elapsedSec)}<span className="aa-time-sep"> / </span>{formatTime(durationSec)}
                    </span>
                </div>

                <div className="article-audio-voice" ref={(r) => { this.menuRef = r; }}>
                    <button
                        type="button"
                        className="article-audio-voicebtn"
                        onClick={() => this.setState({ voiceMenuOpen: !voiceMenuOpen })}
                        aria-label="Changer la voix"
                        title={selected ? `Voix : ${selected.name}` : "Voix"}
                    >
                        <span className="article-audio-voicename">{this.shortVoiceName(selected)}</span>
                        <ChevronDown size={14} />
                    </button>
                    {voiceMenuOpen && (
                        <div className="article-audio-menu" role="listbox">
                            {voices.length === 0 && (
                                <div className="article-audio-menu-empty">Aucune voix disponible</div>
                            )}
                            {voices.map((v) => (
                                <button
                                    key={v.voiceURI}
                                    type="button"
                                    role="option"
                                    aria-selected={v.voiceURI === voiceURI}
                                    className={`article-audio-menu-item ${v.voiceURI === voiceURI ? "is-active" : ""}`}
                                    onClick={() => this.selectVoice(v.voiceURI)}
                                >
                                    <span className="article-audio-check">
                                        {v.voiceURI === voiceURI && <Check size={14} />}
                                    </span>
                                    <span className="article-audio-menu-label">
                                        {v.name}
                                        <small>{v.lang}</small>
                                    </span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <button
                    type="button"
                    className="article-audio-ctrl article-audio-close"
                    onClick={this.toggleOpen}
                    aria-label="Fermer le lecteur"
                    title="Fermer"
                >
                    <X size={16} />
                </button>
              </div>
              </div>
              )}
            </div>
        );
    }
}

export default ArticleAudioPlayer;
